import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, Download, FileWarning, Loader2 } from 'lucide-react';
import {
  buildVersionLine,
  parseFrontmatter,
  type LegalFrontmatter,
} from './frontmatter';
import { applyPageMeta } from './meta';
import {
  getLegalDoc, legalPath, legalVersionPath,
  CURRENT_LEGAL_VERSION, LEGAL_EFFECTIVE_DATE, LEGAL_EFFECTIVE_DATE_LABEL, priorVersionFor,
  type LegalSlug,
} from './legalConfig';

type LoadState = 'loading' | 'ready' | 'notfound' | 'error';

/**
 * <LegalPage /> — renderiza un documento legal servido como estático. Página
 * PÚBLICA (no usa AuthProvider ni el backend).
 *   · sin `version` → versión VIGENTE en `/legal/<slug>.md`
 *   · con `version` (ej. '3.0') → histórico en `/legal/v3.0/<slug>.md` (§16)
 */
export function LegalPage({ documentSlug, version }: { documentSlug: LegalSlug; version?: string }) {
  // La ruta del .md depende de si es la vigente o una histórica.
  const mdPath = version ? `${legalVersionPath(version, documentSlug)}.md` : `${legalPath(documentSlug)}.md`;
  // Banner "entra en vigor" solo en la vigente y solo antes de la fecha de vigencia.
  // El Tarifario queda EXCLUIDO: es un documento nuevo (no tiene versión anterior a la
  // que remitir) y declara su propia vigencia en el encabezado; el banner remitiría a
  // un /legal/v3.2/tarifas inexistente.
  const showEffectiveBanner =
    !version && documentSlug !== 'tarifas' && new Date() < new Date(LEGAL_EFFECTIVE_DATE);
  // Relay Fidelidad v3 — la anterior REAL de cada documento (Aviso 3.2 → rige la 3.1).
  const priorVersion = priorVersionFor(documentSlug);
  const [state, setState] = useState<LoadState>('loading');
  const [content, setContent] = useState('');
  const [fm, setFm] = useState<LegalFrontmatter>({});
  const [downloading, setDownloading] = useState(false);
  const articleRef = useRef<HTMLDivElement>(null);

  const docMeta = getLegalDoc(documentSlug);
  const title = fm.title || docMeta?.title || 'Documento legal';
  const description =
    fm.description || 'Documento legal de FluidSol.';
  const versionLine = buildVersionLine(fm);
  const isDraft = (fm.status || '').toLowerCase() === 'draft';

  // Carga del Markdown.
  useEffect(() => {
    let cancelled = false;
    setState('loading');
    setContent('');
    setFm({});

    fetch(mdPath, {
      headers: { Accept: 'text/markdown, text/plain' },
    })
      .then(async (res) => {
        if (!res.ok) {
          if (!cancelled) setState(res.status === 404 ? 'notfound' : 'error');
          return;
        }
        const text = await res.text();
        // Si el SPA fallback devolvió index.html en vez del .md, trátalo como 404.
        if (
          /^\s*<!doctype html/i.test(text) ||
          text.includes('<div id="root">')
        ) {
          if (!cancelled) setState('notfound');
          return;
        }
        const parsed = parseFrontmatter(text);
        if (cancelled) return;
        setFm(parsed.data);
        setContent(parsed.content);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });

    return () => {
      cancelled = true;
    };
  }, [mdPath]);

  // Meta tags (title + description + OG) cuando el doc está listo.
  useEffect(() => {
    if (state === 'ready') {
      applyPageMeta({ title, description, url: window.location.href });
    }
  }, [state, title, description]);

  async function handleDownloadPdf() {
    if (!articleRef.current || downloading) return;
    setDownloading(true);
    try {
      // Carga diferida: html2pdf es pesado y solo corre en cliente.
      const html2pdf = (await import('html2pdf.js')).default;
      await html2pdf()
        .set({
          margin: [14, 14, 18, 14],
          filename: `${documentSlug}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .from(articleRef.current)
        .save();
    } catch {
      // Fallback simple: imprimir (el usuario puede "Guardar como PDF").
      window.print();
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-slate-800">
      <div className="mx-auto max-w-3xl px-5 py-10 md:py-14">
        {/* Cabecera con marca + acciones (fuera del área imprimible). */}
        <div className="mb-8 flex items-center justify-between gap-4 print:hidden">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition hover:text-slate-800"
          >
            <ArrowLeft className="h-4 w-4" /> Volver
          </a>
          <span className="font-serif text-lg tracking-tight text-slate-900">
            FluidSol
          </span>
          <button
            onClick={handleDownloadPdf}
            disabled={state !== 'ready' || downloading}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {downloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            Descargar PDF
          </button>
        </div>

        {state === 'loading' && (
          <div className="grid place-items-center py-20 text-sm text-slate-500">
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando documento…
            </span>
          </div>
        )}

        {(state === 'notfound' || state === 'error') && (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
            <FileWarning className="mx-auto h-8 w-8 text-amber-500" />
            <h1 className="mt-3 font-serif text-xl text-slate-900">
              {state === 'notfound'
                ? 'Documento no disponible'
                : 'No pudimos cargar el documento'}
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              {state === 'notfound'
                ? 'Este documento legal aún no está publicado.'
                : 'Ocurrió un error al obtener el documento. Intenta de nuevo más tarde.'}
            </p>
            <a
              href="/"
              className="mt-5 inline-block rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
            >
              Ir al inicio
            </a>
          </div>
        )}

        {state === 'ready' && (
          <article
            ref={articleRef}
            className="rounded-2xl border border-slate-200 bg-white px-6 py-8 md:px-10 md:py-12 print:border-0 print:shadow-none"
          >
            {isDraft && (
              <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs font-medium text-amber-800">
                Borrador provisional — pendiente de aprobación legal.
              </div>
            )}

            {/* Histórico (§16): viendo una versión anterior por URL. */}
            {version && (
              <div className="mb-6 rounded-lg border border-slate-300 bg-slate-100 px-4 py-2.5 text-xs font-medium text-slate-700">
                Estás viendo una versión anterior (v{version}). {' '}
                <a href={legalPath(documentSlug)} className="underline">Ver la versión vigente (v{CURRENT_LEGAL_VERSION})</a>.
              </div>
            )}

            {/* Vigente pero con entrada en vigor futura (§16 — 30 días). */}
            {showEffectiveBanner && (
              <div className="mb-6 rounded-lg border border-sky-200 bg-sky-50 px-4 py-2.5 text-xs font-medium text-sky-800">
                Esta versión entra en vigor el {LEGAL_EFFECTIVE_DATE_LABEL}. Hasta entonces sigue vigente la {' '}
                <a href={legalVersionPath(priorVersion, documentSlug)} className="underline">versión anterior (v{priorVersion})</a>.
              </div>
            )}

            <div className="legal-prose">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {content}
              </ReactMarkdown>
            </div>

            {versionLine && (
              <footer className="mt-10 border-t border-slate-200 pt-5 text-xs text-slate-500">
                {versionLine}
              </footer>
            )}
          </article>
        )}
      </div>
    </div>
  );
}

// Render de enlaces: externos en pestaña nueva; internos (/legal, mailto, #)
// como navegación normal.
const mdComponents = {
  a({ href, children, ...props }: React.ComponentPropsWithoutRef<'a'>) {
    const isExternal = !!href && /^https?:\/\//i.test(href);
    return (
      <a
        href={href}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...props}
      >
        {children}
      </a>
    );
  },
};

export default LegalPage;
